import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User } from '../types';
import {
  authService,
  shouldRefreshToken,
  AUTH_LOGIN_REDIRECT_EVENT,
  dispatchAuthLoginRedirect,
  getStoredAccessToken,
  getStoredRefreshToken,
  clearStoredAuthTokens,
  updateStoredAuthTokens,
  storeAuthTokens,
} from '../services/api';
import { parseJwtPayload } from '../utils/jwt';
import { createLogger } from '../services/logger';

const logger = createLogger('auth-context');

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (givenName: string, familyName: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  handleAuthCallback: (token: string, userData?: User, refreshToken?: string, rememberMe?: boolean) => Promise<void>;
  checkTokenValidity: () => boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  refreshUserProfile: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ success: boolean, message: string }>;
  refreshTokenIfNeeded: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false); // Track if auth check completed

  useEffect(() => {
    const handleAuthRedirect = () => {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    };

    window.addEventListener(AUTH_LOGIN_REDIRECT_EVENT, handleAuthRedirect);
    return () => {
      window.removeEventListener(AUTH_LOGIN_REDIRECT_EVENT, handleAuthRedirect);
    };
  }, []);

  // Fonction pour vérifier si un token est présent et valide (non expiré)
  const checkTokenValidity = useCallback((): boolean => {
    const token = getStoredAccessToken();

    if (!token) {
      return false;
    }

    try {
      const payload = parseJwtPayload<{ exp?: number }>(token);
      if (!payload) {
        return false;
      }

      // Vérifier l'expiration
      // Ne PAS supprimer le token ici - laisser l'intercepteur API gérer le refresh
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        return false;
      }

      return true;
    } catch {
      // En cas d'erreur de décodage, le token est malformé mais on ne le supprime pas
      // L'intercepteur API le gérera lors de la prochaine requête
      return false;
    }
  }, []);

  const extractUserFromToken = useCallback((token: string): User | null => {
    try {
      const payload = parseJwtPayload<{
        username?: string;
        email?: string;
        name?: string;
        givenName?: string;
        familyName?: string;
      }>(token);
      if (!payload) {
        return null;
      }
      const identity = payload.username || payload.email;

      if (!identity) {
        return null;
      }

      return {
        email: identity,
        name: payload.name || identity,
        givenName: payload.givenName || '',
        familyName: payload.familyName || '',
      };
    } catch {
      return null;
    }
  }, []);

  // Fonction pour rafraîchir les données du profil utilisateur
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    if (!checkTokenValidity() || !isInitialized) {
      return;
    }

    try {
      setLoading(true);
      // Récupérer les informations de l'utilisateur depuis l'API
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (err) {
      logger.error('Erreur lors du rafraîchissement du profil utilisateur', err);
    } finally {
      setLoading(false);
    }
  }, [checkTokenValidity, isInitialized]);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);

      const isValid = checkTokenValidity();
      const refreshToken = getStoredRefreshToken();

      if (isValid) {
        try {
          // D'abord essayer d'extraire les infos utilisateur du token JWT
          const token = getStoredAccessToken();
          if (token) {
            const userFromToken = extractUserFromToken(token);
            if (userFromToken) {
              setUser(userFromToken);
              setIsAuthenticated(true);
              setLoading(false);
              setIsInitialized(true);
              return;
            }
          }

          // Si les infos ne sont pas dans le token ou sont insuffisantes, faire une requête API
          try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
          } catch (apiErr) {
            logger.error('Failed to get user data from API', apiErr);
            // Si on a un refresh token, essayer de l'utiliser
            if (refreshToken) {
              try {
                logger.debug('API call failed but refresh token available, trying refresh');
                const refreshResult = await authService.refreshToken();
                if (refreshResult.token) {
                  const userData = await authService.getCurrentUser();
                  setUser(userData);
                  setIsAuthenticated(true);
                  logger.debug('Successfully refreshed token and got user data');
                }
              } catch (refreshErr) {
                logger.error('Token refresh failed', refreshErr);
                // Nettoyer les tokens invalides
                clearStoredAuthTokens();
                setUser(null);
                setIsAuthenticated(false);
              }
            } else {
              // Pas de refresh token, garder l'état d'authentification mais sans données utilisateur
              setIsAuthenticated(true);
            }
          }
        } catch (err) {
          logger.error('Error checking auth status', err);
          clearStoredAuthTokens();
          setUser(null);
          setIsAuthenticated(false);
        }
      } else if (refreshToken) {
        // Access token invalide ou inexistant, mais refresh token disponible
        try {
          logger.debug('Access token invalid/missing, trying refresh token');
          const refreshResult = await authService.refreshToken();
          if (refreshResult.token) {
            const userData = await authService.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            logger.debug('Successfully used refresh token for authentication');
          }
        } catch (refreshErr) {
          logger.error('Refresh token also invalid', refreshErr);
          clearStoredAuthTokens();
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        // Re-vérifier juste avant de conclure, pour éviter les races avec un callback OAuth en cours.
        if (checkTokenValidity()) {
          const latestToken = getStoredAccessToken();
          if (latestToken) {
            const userFromToken = extractUserFromToken(latestToken);
            if (userFromToken) {
              setUser(userFromToken);
            }
            setIsAuthenticated(true);
          }
        } else {
          // Aucun token valide
          logger.debug('No valid tokens found');
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setLoading(false);
      setIsInitialized(true); // Mark initialization as complete
    };

    checkAuth();
  }, [checkTokenValidity, extractUserFromToken]); // Run only once on mount

  // Fonction pour renouveler le token si nécessaire
  const refreshTokenIfNeeded = useCallback(async (): Promise<boolean> => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      logger.debug('No refresh token available');
      return false;
    }

    if (!shouldRefreshToken()) {
      return true; // Token n'a pas besoin d'être renouvelé
    }

    try {
      logger.debug('Refreshing token');
      const response = await authService.refreshToken();

      if (response.token) {
        updateStoredAuthTokens(response.token, response.refresh_token);

        // Mettre à jour les données utilisateur si elles sont fournies
        if (response.user) {
          setUser(response.user);
        }

        logger.debug('Token refreshed successfully');
        return true;
      }

      return false;
    } catch (err) {
      logger.error('Token refresh failed', err);
      // Ne pas déconnecter automatiquement ici
      // L'intercepteur API gérera la déconnexion si nécessaire
      return false;
    }
  }, []);

  // Effet pour surveiller et renouveler le token périodiquement
  useEffect(() => {
    if (!isAuthenticated || !isInitialized) return;

    logger.debug('AuthContext: Starting token monitoring');

    // Vérifier le token toutes les 5 minutes (more reasonable than 1 minute)
    const tokenCheckInterval = setInterval(async () => {
      if (shouldRefreshToken()) {
        logger.debug('AuthContext: Token needs refresh, calling refreshTokenIfNeeded');
        await refreshTokenIfNeeded();
      }
    }, 300000); // 5 minutes

    return () => clearInterval(tokenCheckInterval);
  }, [isAuthenticated, isInitialized, refreshTokenIfNeeded]);

  // Login avec email et mot de passe
  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(email, password, rememberMe);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (err: unknown) {
      setError((err as Error).message || 'Une erreur est survenue lors de la connexion');
      throw err;
    } finally {
      setLoading(false);
    }
  };


  // Inscription
  const register = async (givenName: string, familyName: string, email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.register(email, password, givenName, familyName);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (err: unknown) {
      setError((err as Error).message || 'Une erreur est survenue lors de l\'inscription');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Demande de réinitialisation de mot de passe
  const requestPasswordReset = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.requestPasswordReset(email);
      return response;
    } catch (err: unknown) {
      setError((err as Error).message || 'Une erreur est survenue lors de la demande de réinitialisation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Réinitialisation du mot de passe avec un token
  const resetPassword = async (token: string, newPassword: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.resetPassword(token, newPassword);
      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue lors de la réinitialisation du mot de passe';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const logout = () => {
    clearStoredAuthTokens();
    setUser(null);
    setIsAuthenticated(false);

    // Clear flight list state on logout
    try {
      sessionStorage.removeItem('flightListState');
    } catch (error) {
      logger.warn('Failed to clear flight list state on logout', error);
    }

    dispatchAuthLoginRedirect('manual-logout');
  };

  // Gestion du callback OAuth
  const handleAuthCallback = useCallback(async (token: string, userData?: User, refreshToken?: string, rememberMe?: boolean): Promise<void> => {
    setError(null);
    if (typeof rememberMe === 'boolean') {
      storeAuthTokens(token, refreshToken, rememberMe);
    } else {
      updateStoredAuthTokens(token, refreshToken);
    }

    // Marquer immédiatement la session comme authentifiée, puis hydrater le profil.
    setIsAuthenticated(true);

    // Si les données utilisateur sont fournies avec le token, les utiliser directement
    if (userData) {
      setUser(userData);
      return;
    }

    // Sinon, vérifier la validité du token et récupérer les données utilisateur
    if (!checkTokenValidity()) {
      clearStoredAuthTokens();
      setUser(null);
      setIsAuthenticated(false);
      throw new Error('Invalid authentication token');
    }

    const userFromToken = extractUserFromToken(token);
    if (userFromToken) {
      setUser(userFromToken);
      return;
    }

    try {
      const profile = await authService.getCurrentUser();
      setUser(profile);
    } catch (err) {
      logger.error('Error getting user data after auth callback', err);
      setError('Connexion établie, mais le profil utilisateur est indisponible temporairement.');
    }
  }, [checkTokenValidity, extractUserFromToken]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      register,
      logout,
      handleAuthCallback,
      checkTokenValidity,
      isAuthenticated,
      isInitialized,
      refreshUserProfile,
      requestPasswordReset,
      resetPassword,
      refreshTokenIfNeeded
    }}>
      {children}
    </AuthContext.Provider>
  );
};